import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { LoginContext } from '../context/LoginContext';
import SidebarAdmin from './Utils/SidebarAdmin';
import LogoutButton from './LogoutButton';
import apiConfig from '../config/apiConfig';
import { apiFetch } from '../apis/apiFetch';
import { ApplicationRow } from './Applications';
import { AgGridReact } from 'ag-grid-react';
import { GridApi } from 'ag-grid-community';
import { toast } from '../utils/toast';
import { Application, formatDateDDMMYYYY, Round } from './Resource/ViewRounds';
import { ColumnDef } from './Resource/UpdateDrive';
import * as XLSX from "xlsx";
import { withdrawalsService } from '../services/WithdrawalService';
import { useQuery } from '@tanstack/react-query';
import { useDriveViewModel } from '../viewModels/useDriveViewModel';
import { useParams } from 'react-router-dom';

const requiredFields = ["application_number", "fullname", "offer_status", "isAdmitted", "offered_programme"]

const FIELD_LABELS: Record<string, string> = {
    // Personal Details (Applicant)
    application_number: "Application Number",
    fullname: "Full Name",
    email: "Email",
    mobile: "Mobile",
    dob: "Date of Birth",
    gender: "Gender",
    photo: "Photo",
    nationality: "Nationality",
    // Address
    line1: "Address Line 1",
    line2: "Address Line 2",
    city: "City",
    state: "State",
    pincode: "Pincode",
    country: "Country",
    // Guardian
    name: "Name",
    relation: "Relation",
    alternatemobile: "Alternate Mobile",
    // Graduation
    collegename: "College Name",
    degree: "Degree",
    major: "Branch",
    cgpa: "Percentage",
    passingyear: "Year of Passing",
    marks_obtained: "Marks/ CGPA Obtained",
    maximum_marks: "Maximum Marks/CGPA",
    // Programme Preference
    pref1: "Programme 1",
    pref2: "Programme 2",
    pref3: "Programme 3",
    pref1scorecard: "Programme 1 Scorecard",
    pref2scorecard: "Programme 2 Scorecard",
    pref3scorecard: "Programme 3 Scorecard",
    pref1rollno: "Programme 1 Roll No.",
    pref2rollno: "Programme 2 Roll No.",
    pref3rollno: "Programme 3 Roll No.",
    pref1score: "Programme 1 Score",
    pref2score: "Programme 2 Score",
    pref3score: "Programme 3 Score",
    pref1rank: "Programme 1 Rank",
    pref2rank: "Programme 2 Rank",
    pref3rank: "Programme 3 Rank",
    pref1subjects: "Programme 1 Subject",
    pref2subjects: "Programme 2 Subject",
    pref3subjects: "Programme 3 Subject",
    pref1year: "Programme 1 Year",
    pref2year: "Programme 2 Year",
    pref3year: "Programme 3 Year",
    // GATE
    subjects: "Subject",
    score: "GATE Score",
    rank: "GATE Rank",
    rollno: "GATE Roll No.",
    scorecard: "Scorecard",
    // Payment
    transaction_id: "Transaction ID",
    amount: "Amount",
    status: "Status",
    payment_date: "Payment Date",
    payment_mode: "Payment Mode",
    reference_no: "Reference No.",
    user_id: "User ID",
    // BTech Specific Labels
    edu10_board: "Education Board",
    edu10_schoolname: "School/College",
    edu10_passingyear: "Year of Passing",
    edu10_marksformat: "Grade/Marks Type",
    edu10_marksobtained: "Marks Obtained",
    edu10_maximummarks: "Maximum Marks",
    edu10_percentage: "Percentage",
    edu10_grade: "Grade",
    edu12_board: "Education Board",
    edu12_schoolname: "School/College",
    edu12_passingyear: "Year of Passing",
    edu12_specialization: "Specialization",
    edu12_marksformat: "Grade/Marks Type",
    edu12_marksobtained: "Marks Obtained",
    edu12_maximummarks: "Maximum Marks",
    edu12_percentage: "Percentage",
    edu12_grade: "Grade",
    edu12_percentagemaths: "Mathematics Percentage",
    pref4: "Programme 4",
    pref5: "Programme 5",
    jeemain_rollno: "JEE Main Application No.",
    jeemain_score: "JEE Main NTA Final Total Score",
    jeemain_rank: "JEE Main Rank (CRL)",
    jeemain_scorecard: "JEE Main Scorecard",
    jeeadv_rollno: "JEE Advanced Roll No.",
    jeeadv_score: "JEE Advanced Score",
    jeeadv_rank: "JEE Advanced Rank (CRL)",
    jeeadv_scorecard: "JEE Advanced Scorecard",
    olympiad_name: "Olympiad Name",
    olympiad_no: "Olympiad Roll No.",
    olympiad_proof: "Olympiad Proof",
    olympiad_year: "Olympiad Year",
    // Internal Subheaders
    _edu10_header: "Class Xth / SSC",
    _edu12_header: "Class XIIth / HSC",
    _jeemain_header: "JEE Main",
    _jeeadv_header: "JEE Advanced (Optional)",
    _olympiad_header: "Olympiad Details (Optional)",
    isAdmitted: "Admitted",
    final_programme: "Accepted Programme",
    final_round: "Accepted Round",
};

interface Option {
    value: string;
    label: string;
}

function UpdateStatus() {
    const { user } = useContext(LoginContext);
    const { appId }: any = useParams<any>();

    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    const [loading, setLoading] = useState(false);
    const [updating, setIsUpdating] = useState(false);
    const [allApplications, setAllApplications] = useState<Application[]>([]);
    const [colDef1, setColDef1] = useState<any[]>([]);
    const defaultColDef = {
        flex: 1,
        minWidth: 100,
        editable: false,
    };

    const [filterOfferStatus, setFilterOfferStatus] = useState("ALL");
    const [filterProgramme, setFilterProgramme] = useState("ALL");
    const [filterRound, setFilterRound] = useState("ALL");
    const [roundNames, setRoundNames] = useState<Option[]>([]);
    const [programmes, setProgrammes] = useState<Option[]>([]);
    const [driveType, setDriveType] = useState("");
    const [selectedRound, setSelectedRound] = useState<string>("");
    const [rounds, setRounds] = useState<Round[]>([]);
    const [drives, setDrives] = useState<any[]>([]);
    const [exporting, setExporting] = useState(false);
    const [showWithdrawnOnly, setShowWithdrawnOnly] = useState(false);

    useEffect(() => {
        if (driveType === "Mtech") {
            setRoundNames([
                { value: "ALL", label: "All Rounds" },
                { value: "M.Tech-Online-Round-1", label: "M.Tech Online Round 1" },
                { value: "M.Tech-2026-Round-3", label: "M.Tech Round 3" },
                { value: "M.Tech-2026-Round 2", label: "M.Tech Round 2" },
                { value: "M.Tech-2026-Round 1", label: "M.Tech Round 1" },
            ]);

            setProgrammes([
                { value: "ALL", label: "All Programmes" },
                { value: "MTech_CSE", label: "MTech CSE" },
                { value: "MTech_ECE", label: "MTech ECE" },
                { value: "MTech_AI_DS", label: "MTech AI & DS" },
            ]);
        } else {
            setRoundNames([
                { value: "ALL", label: "All Rounds" },
                { value: "B.Tech-IMTech-Online-Round-1", label: "B.Tech Online Round 1" },
                { value: "B.Tech-IMTech-2026-Round-3", label: "B.Tech Round 3" },
                { value: "B.Tech-IMTech-2026-Round-2", label: "B.Tech Round 2" },
                { value: "B.Tech-IMtech-2026-Round-1", label: "B.Tech Round 1" },
            ]);

            setProgrammes([
                { value: "ALL", label: "All Programmes" },
                { value: "BTech_CSE", label: "BTech CSE" },
                { value: "BTech_ECE", label: "BTech ECE" },
                { value: "BTech_AI_DS", label: "BTech AI & DS" },
                { value: "IMTech_CSE", label: "IMTech CSE" },
                { value: "IMTech_ECE", label: "IMTech ECE" },
            ]);
        }
    }, [driveType]);

    const gridApiRef = useRef<GridApi | null>(null);
    const onGridReady = (params: any) => {
        gridApiRef.current = params.api;
    };

    const onSelectionChanged = () => {
        const selectedRows = gridApiRef.current?.getSelectedRows() || [];
    }

    const executeUpdateStatus = async () => {
        setIsUpdating(true);

        try {
            const selectedRows = gridApiRef.current?.getSelectedRows() || [];

            if (selectedRows.length === 0) {
                toast.error("Please select at least one application.");
                return;
            }

            const BATCH_SIZE = 30;
            let successCount = 0;
            let failedCount = 0;

            for (let i = 0; i < selectedRows.length; i += BATCH_SIZE) {
                const batch = selectedRows.slice(i, i + BATCH_SIZE);

                const batchApplicationNumbers = batch
                    .map((row) => row.id)
                    .join(";");

                try {
                    const args = `application_numbers:${batchApplicationNumbers}`;

                    const url = `${apiConfig.getResourceUrl(
                        "applications"
                    )}?queryId=ADMIT_APPLICATIONS&args=${encodeURIComponent(args)}`;

                    const response = await apiFetch(url, { method: "GET" });
                    const res = await response.json();

                    if (res?.errCode === 0) {
                        successCount += batch.length;
                    } else {
                        failedCount += batch.length;
                    }
                } catch (err) {
                    console.error(err);
                    failedCount += batch.length;
                }

                await new Promise((resolve) => setTimeout(resolve, 300));
            }

            toast.success(`${successCount} applications updated successfully.`);

            if (failedCount > 0) {
                toast.error(`${failedCount} applications failed to update.`);
            }

            fetchApplications(driveType);
        } catch (err) {
            console.error("Update failed", err);
            toast.error("An error occurred while updating.");
        } finally {
            setIsUpdating(false);
        }
    };

    const {
        enums,
    } = useDriveViewModel(user?.userId || "", appId);

    useEffect(() => {
        const fetchRounds = async () => {
            try {
                const url = `${apiConfig.getResourceUrl("rounds")}?queryId=GET_ALL`;
                const response = await apiFetch(url);
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const data = await response.json();
                let rawRounds = [];
                if (Array.isArray(data)) rawRounds = data;
                else if (data && Array.isArray(data.resource)) rawRounds = data.resource;
                else if (data && Array.isArray(data.records)) rawRounds = data.records;

                const sorted = rawRounds.sort((a: any, b: any) => {
                    const parseDate = (val: any) => {
                        if (!val) return 0;
                        const d = new Date(val);
                        if (!isNaN(d.getTime())) return d.getTime();
                        const n = Number(val);
                        if (!isNaN(n)) return n;
                        return 0;
                    };
                    return parseDate(b.startdate || b.date) - parseDate(a.startdate || a.date);
                });
                setRounds(sorted);
            } catch (err) {
                console.error("ViewRounds: Error fetching rounds", err);
            }
        };
        const fetchDrives = async () => {
            try {
                const url = `${apiConfig.getResourceUrl("drive")}?queryId=GET_ALL`;
                const response = await apiFetch(url);
                if (response.ok) {
                    const data = await response.json();
                    let rawDrives = [];
                    if (Array.isArray(data)) rawDrives = data;
                    else if (data && Array.isArray(data.resource)) rawDrives = data.resource;
                    else if (data && Array.isArray(data.records)) rawDrives = data.records;
                    setDrives(rawDrives);
                }
            } catch (err) {
                console.error("ViewRounds: Error fetching drives", err);
            }
        };
        fetchRounds();
        fetchDrives();
    }, []);

    useEffect(() => {
        if (driveType !== null || driveType !== "") {
            console.log("driveType", driveType)
            fetchApplications(driveType);
        }
    }, [driveType])
    const { data: dataRes, isLoading: isLoadingDataRes, error: errorDataRes, refetch } = useQuery({
        queryKey: ['resourceData', 'withdrawalsRead'],
        queryFn: async () => {
            const response = await withdrawalsService.getData()
            return response;
        },
    })
    useEffect(() => {
        const columns = [

            ...requiredFields.map((field: string) => ({
                field,
                headerName:
                    field === "application_number"
                        ? "Application Number" :
                        field === "offer_status"
                            ? "Offer Status" :
                            field === "isAdmitted"
                                ? "Admitted" :
                                field === "fullname"
                                    ? "Name" :
                                    field === "offered_programme"
                                        ? "Offered Programme"
                                        : field.charAt(0).toUpperCase() + field.slice(1),
                editable: false,
                resizable: true,
                sortable: true,
                filter: true,
                ...(field === "offer_status" && {
                    cellRenderer: (params: any) => {
                        return (
                            <div>
                                {params.data.extra_data?.final_status}
                            </div>
                        );
                    },
                }),
                ...(field === "offered_programme" && {
                    cellRenderer: (params: any) => {
                        return (
                            <div>
                                {params.data.extra_data?.final_programme}
                            </div>
                        );
                    },
                }),
            })),
        ];

        setColDef1(columns);
    }, []);

    const fetchApplications = async (driveType: string) => {
        setLoading(true);
        try {
            const args = `driveType:${driveType}`;
            const url = `${apiConfig.getResourceUrl("applications")}?queryId=GET_ADMIT_APPLICATIONS&args=${encodeURIComponent(args)}`;

            const res = await apiFetch(url, { method: "GET" });
            if (res.ok) {
                const data = await res.json();
                const resources: Application[] = data.resource || [];

                // Filter out carrier-only records
                const validApps = resources.filter(
                    (r) => r.application_number && r.application_number !== "PENDING",
                );
                setAllApplications(validApps);
            }
        } catch (err) {
            console.error("Error fetching applications:", err);
        } finally {
            setLoading(false);
        }
    };
    const isBtech = driveType === "Btech";
    const filteredApplications = useMemo(() => {
        let list = allApplications;
        const withdrawnApplicationNumbers = isBtech ? new Set(
            dataRes?.filter((app: any) => !app.app_id?.startsWith("MT2026"))?.map((app: any) => app.app_id)
        ) : new Set(
            dataRes?.filter((app: any) => app.app_id?.startsWith("MT2026"))?.map((app: any) => app.app_id)
        );


        console.log("withdrawn count", withdrawnApplicationNumbers.size);

        const matched = allApplications.filter(app =>
            withdrawnApplicationNumbers.has(app.application_number)
        );

        console.log("matched", matched.length);

        const missing = [...withdrawnApplicationNumbers].filter(
            id => !allApplications.some(app => app.application_number === id)
        );

        console.log("missing ids", missing);
        // console.log("firstfirst", dataRes?.filter((app: any) => app.app_id?.startsWith("MT2026"))?.map((app: any) => app.app_id))
        list = list.filter(app => {
            const extraData = app.extra_data || {};

            if (
                filterProgramme !== "ALL" &&
                extraData?.final_programme !== filterProgramme
            ) {
                return false;
            }

            if (
                filterRound !== "ALL" &&
                extraData?.final_round !== filterRound
            ) {
                return false;
            }
            // Filter by status if needed
            if (
                filterOfferStatus !== "ALL" &&
                extraData?.final_status !== filterOfferStatus
            ) {
                return false;
            }
            return true;
        });
        if (showWithdrawnOnly) {
            list = list.filter(
                app => !withdrawnApplicationNumbers.has(app.application_number)
            );
        }
        list.map(app => ({
            ...app, isAdmitted: app.isAdmitted ?? false,
        }))
        return list;
    }, [allApplications, filterProgramme, filterRound, filterOfferStatus, dataRes, showWithdrawnOnly, isBtech]);

    const getHeader = (key: string) =>
        key
            .replace(/_/g, " ")
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            .replace(/\b\w/g, c => c.toUpperCase());


    const exportApplications = (
        applications: any[],
        FIELD_LABELS: Record<string, string>,
        fileName = `Applications_Accepted_${new Date().toISOString().split("T")[0]}.xlsx`
    ) => {
        setExporting(true);

        try {
            const rows = applications.map((app) => {
                const row: Record<string, any> = {};

                Object.entries(FIELD_LABELS).forEach(([fieldKey, header]) => {
                    let value;

                    if (fieldKey === "final_programme" || fieldKey === "final_round") {
                        value = app.extra_data?.[fieldKey];
                    } else {
                        value = fieldKey in app
                            ? app[fieldKey]
                            : app.extra_data?.[fieldKey];
                    }

                    row[header] = value ?? "";
                });

                return row;
            });

            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                "Applications"
            );

            XLSX.writeFile(workbook, fileName);
        } catch (err) {
            console.error("Export error:", err);
            toast.error("Failed to export data");
        } finally {
            setExporting(false);
        }
    };

    const handleExport = () => {
        const selectedRows = gridApiRef.current?.getSelectedRows() || [];

        if (selectedRows.length === 0) {
            toast.error("Please select at least one application.");
            return;
        }
        if (gridApiRef.current?.getSelectedRows()) {
            exportApplications(gridApiRef.current?.getSelectedRows(), FIELD_LABELS)
        }
    }
    return (
        <div className="dash-layout">
            {/* Sidebar */}
            <SidebarAdmin
                sidebarCollapsed={sidebarCollapsed}
                toggleSidebar={() => setSidebarCollapsed((p) => !p)}
            />

            {/* Main Wrapper */}
            <div
                className={`dash-wrapper ${sidebarCollapsed ? "dash-wrapper-collapsed" : ""
                    }`}
            >
                {/* Header */}
                <header className="dash-header">
                    <h1 className="dash-page-title">Admit Status</h1>

                    <div className="dash-user-profile">
                        <div
                            className="dash-profile-circle"
                            onClick={() => setShowDropdown((p) => !p)}
                        >
                            <span>A</span>
                        </div>

                        {showDropdown && (
                            <div className="dash-profile-dropdown">
                                <LogoutButton />
                            </div>
                        )}
                    </div>
                </header>

                <div >
                    <div
                        style={{ display: "flex", justifyContent: "space-between" }}
                    >
                        <div style={{
                            display: "flex",
                            gap: "12px",
                            padding: "12px 16px",
                            borderBottom: "1px solid #e5e7eb",
                            flexWrap: "wrap",
                            alignItems: "center",
                        }}>
                            {/* <select
                                value={selectedRound}
                                onChange={onRoundChange}
                                style={{
                                    padding: "6px 10px",
                                    borderRadius: "6px",
                                    border: "1px solid #d1d5db",
                                    fontSize: "13px",
                                    color: "#374151",
                                    backgroundColor: "#fff",
                                    minWidth: "140px",
                                }}
                            >
                                <option value="">Choose Round</option>
                                {rounds.filter(r => r && r.id).map((r) => (
                                    <option key={r.id} value={r.id}>
                                        {r.name || `Round ${r.id}`}{r.allocation_mode === "RECOVERY" ? " [Revised]" : r.allocation_mode === "SLIDING" ? " [Sliding]" : ""} ({formatDateDDMMYYYY(r.startdate || r.date)} to {formatDateDDMMYYYY(r.enddate || r.date)})
                                    </option>
                                ))}
                            </select> */}
                            {/* <div className={styles.formGroup}/> */}
                            {/* <label className={styles.formLabel}>
                                    <span className={styles.required}>*</span> Drive Type
                                </label> */}
                            <select
                                style={{
                                    padding: "6px 10px",
                                    borderRadius: "6px",
                                    border: "1px solid #d1d5db",
                                    fontSize: "13px",
                                    color: "#374151",
                                    backgroundColor: "#fff",
                                    minWidth: "140px",
                                }}
                                value={driveType}
                                onChange={(e) => setDriveType(e.target.value)}
                            >
                                <option value="">Select Drive</option>
                                <option value="Mtech">M.Tech</option>
                                <option value="Btech">B.Tech</option>
                            </select>
                            <select
                                value={filterRound}
                                onChange={(e) => setFilterRound(e.target.value)}
                                style={{
                                    padding: "6px 10px",
                                    borderRadius: "6px",
                                    border: "1px solid #d1d5db",
                                    fontSize: "13px",
                                    color: "#374151",
                                    backgroundColor: "#fff",
                                    minWidth: "220px",
                                }}
                            >
                                {roundNames.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={filterProgramme}
                                onChange={(e) => setFilterProgramme(e.target.value)}
                                style={{
                                    padding: "6px 10px",
                                    borderRadius: "6px",
                                    border: "1px solid #d1d5db",
                                    fontSize: "13px",
                                    color: "#374151",
                                    backgroundColor: "#fff",
                                    minWidth: "160px",
                                }}
                            >
                                {programmes.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}                            </select>
                            {/* <select
                                value={filterOfferStatus}
                                onChange={(e) => setFilterOfferStatus(e.target.value)}
                                style={{
                                    padding: "6px 10px",
                                    borderRadius: "6px",
                                    border: "1px solid #d1d5db",
                                    fontSize: "13px",
                                    color: "#374151",
                                    backgroundColor: "#fff",
                                    minWidth: "140px",
                                }}
                            >
                                <option value="ALL">All Statuses</option>
                                <option value="ACCEPTED">Accepted</option>
                                <option value="REJECTED">Rejected</option>
                                <option value="OFFER_CANCELLED">Offer Cancelled</option>
                                <option value="WITHDRAWN">Withdrawn</option>
                            </select> */}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                            <label
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    cursor: "pointer",
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={showWithdrawnOnly}
                                    onChange={(e) => setShowWithdrawnOnly(e.target.checked)}
                                />
                                Remove Withdrawn Applications
                            </label>
                            <button
                                className="btn btn-success"
                                onClick={handleExport}
                                disabled={!showWithdrawnOnly || exporting}
                            >
                                Export
                            </button>
                            <button
                                className="btn btn-primary btn-sm fw-medium px-4 text-white border-0 shadow-sm me-2"
                                onClick={executeUpdateStatus}
                                disabled={!showWithdrawnOnly}
                                // disabled={selectedRoundType === "Online" || isConfirming || !selectedRound || selectedIds.length === 0}
                                style={{ borderRadius: "6px", height: "36px", backgroundColor: "#3b82f6", margin: 10 }}
                            >
                                {updating ? "Admitting..." : "Admit"}
                            </button>
                        </div>
                    </div>
                    {loading ?
                        <div>Loading...</div> :
                        filteredApplications.length === 0 && colDef1.length === 0 ? (
                            <div>No data available. Please add a resource attribute.</div>
                        ) : (
                            <div className="ag-theme-alpine" style={{ height: "80vh", width: '100%' }}>
                                <AgGridReact
                                    rowData={filteredApplications}
                                    columnDefs={colDef1}
                                    onGridReady={onGridReady}
                                    onSelectionChanged={onSelectionChanged}
                                    defaultColDef={defaultColDef}
                                    rowSelection={{
                                        mode: "multiRow",
                                    }}
                                    pagination={true}
                                    paginationPageSize={50}
                                    animateRows={true}
                                />
                            </div>
                        )}
                </div>
            </div>
        </div >
    );
}

export default UpdateStatus